import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type MouseEvent } from 'react'
import { customersPageQuery, customersTableQuery, setCustomerLicense } from '../api/easeedeskCustomers'
import { agentLinkText, sendAgentLink } from '../api/settingsContacts'
import { DataTable, useTableState } from '../ui/DataTable'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Content } from '../ui/Shell'
import { toast } from '../ui/notify'
import { customerColumns } from './customerColumns'

/**
 * "Customers" - `Easeedesk::customers`, macheta
 * `views/version-3/easeedesk/customers/index.php` si tabelul
 * `OwnersSupportPage.initDatatable` din
 * `resources/js/app/modules/easeedesk/customers.js`.
 *
 * Acelasi tabel (`owners_licenses`), aceleasi coloane, aceeasi ordine
 * implicita (dupa Firstname, descrescator) si aceeasi cautare pe server.
 *
 * Cele doua butoane de licenta merg si aici, cu aceeasi intrebare inainte si
 * cu acelasi drept ca in portal (`settings.licenses.edit`).
 *
 * CE A RAMAS IN PORTAL, SI DE CE
 * ------------------------------
 * "Create" si "Edit" pe contact, "Add license" / "Edit license" si "Pay"
 * deschideau formulare ale modulului SETTINGS
 * (`settings/contacts/...`, `settings/licenses/ownerSupportLicenseForm`).
 * Alea se muta cu Settings; pana atunci raman unde sunt, si butoanele lor nu
 * se deseneaza aici.
 *
 * "SEND AGENT DOWNLOAD LINK" E AICI (11 septembrie 2026): bifele din coloana
 * 14 (`select-user`, capul `all-users`, `CustomersMultiActions` in app.js) si
 * butonul "Actions" din bara de sus (`PageButtons.multiActionsBtn`), stins
 * pana e bifat cineva. Trimite `contacts[]` la
 * `POST /settings/contacts/agent-link` si spune, ca toastr-ul vechi, ce a
 * plecat si ce nu.
 *
 * Vezi si `customerColumns.ts`.
 */
export function CustomersPage() {
  const page = useQuery(customersPageQuery)
  const client = useQueryClient()

  /* `order: [[1, 'desc']]` si `stateSave: true`, ca in portal. */
  const [state, setState] = useTableState('ownersLicenses', {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })

  const table = useQuery({
    ...customersTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
    enabled: page.data?.ready !== false,
  })

  /* Bifele de selectie multipla (`CustomersMultiActions`): capul bifeaza toate. */
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [actionsOpen, setActionsOpen] = useState(false)
  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const agentLink = useMutation({
    mutationFn: (contacts: number[]) => sendAgentLink(contacts),
    onSuccess: (result) => {
      const said = agentLinkText(result)
      toast(said.text, 'Download agent link', said.ok ? 'success' : 'error')
      setSelected(new Set())
    },
    onError: (e: Error) => toast(e.message, 'Download agent link', 'error'),
  })

  const license = useMutation({
    mutationFn: ({ owner, disabled }: { owner: number; disabled: boolean }) => setCustomerLicense(owner, disabled),
    onSuccess: (_data, sent) => {
      void client.invalidateQueries({ queryKey: ['easeedesk', 'customers', 'table'] })
      toast(sent.disabled ? 'License disabled.' : 'License enabled.', 'Success')
    },
    onError: (e: Error, sent) =>
      toast(sent.disabled ? 'License can not be disabled.' : 'License can not be enabled.', e.message, 'error'),
  })

  /*
   * Butoanele stau in HTML-ul celulei (asa deseneaza `DataTable` coloanele),
   * deci clicul se prinde pe invelis, ca `$table.on('click', selector)` in
   * portal.
   */
  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('select-user')) {
      const id = Number(target.getAttribute('data-id'))
      setSelected((s) => {
        const next = new Set(s)
        if (target.checked) next.add(id)
        else next.delete(id)
        return next
      })
      return
    }
    if (target instanceof HTMLInputElement && target.classList.contains('all-users')) {
      setSelected(target.checked ? new Set(rows.map((r) => Number(r.ID))) : new Set())
      return
    }
    const button = target.closest('[data-license]')
    if (!button) return
    const owner = Number(button.getAttribute('data-owner'))
    const disabled = button.getAttribute('data-license') === 'disable'
    if (owner < 1) return
    if (!window.confirm(disabled ? 'Are you sure? Yes, disable license!' : 'Are you sure? Yes, enable license!')) return
    license.mutate({ owner, disabled })
  }

  if (isForbidden(page.error)) return <Forbidden />

  return (
    <Content>
      {page.data?.ready === false ? (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle"></i> The customer list is not available on this installation.
        </div>
      ) : (
        <div id="content" onClick={onClick}>
          <DataTable
            id="owners_licenses"
            columnTools
            columns={customerColumns()}
            rows={rows}
            headerButtons={
              <div className={'btn-group tk-header-extra' + (actionsOpen ? ' open' : '')} style={{ marginRight: 4 }}>
                <button
                  type="button"
                  className="btn btn-default dropdown-toggle multi-actions"
                  disabled={selected.size === 0 || agentLink.isPending}
                  aria-haspopup="true"
                  aria-expanded={actionsOpen}
                  onClick={() => setActionsOpen((o) => !o)}
                >
                  Actions <span className="caret"></span>
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <a
                      href="#"
                      id="send-agent-download-link"
                      onClick={(e) => {
                        e.preventDefault()
                        setActionsOpen(false)
                        agentLink.mutate([...selected])
                      }}
                    >
                      <i className="far fa-envelope" aria-hidden="true"></i> Send agent download link
                    </a>
                  </li>
                </ul>
              </div>
            }
            total={table.data?.recordsTotal ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            onRetry={() => void table.refetch()}
            state={state}
            onState={setState}
            rowId={(r) => String(r.DT_RowId ?? r.ID)}
            footerButtons={{ copy: true, colvis: true, refresh: () => void table.refetch() }}
          />
        </div>
      )}
    </Content>
  )
}
